'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { AlertTriangle, TrendingUp, X, Check } from 'lucide-react';

const oldWayItems = [
  { text: 'Qualitative ratings', detail: 'High, Medium, Low' },
  { text: 'Subjective assessments', detail: 'Guesswork and opinions' },
  { text: 'Spreadsheet chaos', detail: 'Inconsistent formats' },
  { text: 'Board confusion', detail: 'Technical jargon' },
];

const newWayItems = [
  { text: 'Precise currency values', detail: '£2,847,500/day' },
  { text: 'Data-driven analysis', detail: 'Calculated from your inputs' },
  { text: 'Guided assessment', detail: 'Step-by-step journey' },
  { text: 'Board-ready reports', detail: 'Executive summaries' },
];

export default function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-mesh" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/20 text-sm font-medium text-[hsl(var(--destructive))] mb-6"
          >
            <AlertTriangle className="w-4 h-4" />
            The Problem
          </motion.div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance">
            Traditional BIA Tools Give You{' '}
            <span className="text-[hsl(var(--destructive))]">Guesswork</span>
          </h2>

          <p className="text-lg text-[hsl(var(--slate-medium))] leading-relaxed">
            When your board asks "What does downtime cost us?", answering "It's a
            high-risk system" doesn't cut it. You need real numbers.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Old Way Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-white rounded-2xl p-8 border border-[hsl(var(--border))] shadow-lg overflow-hidden">
              {/* Crossed out effect */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0 flex items-center justify-center">
                  <X className="w-64 h-64 text-[hsl(var(--destructive))] stroke-[0.5]" />
                </div>
              </div>

              <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--destructive))]/10 flex items-center justify-center">
                    <X className="w-6 h-6 text-[hsl(var(--destructive))]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-medium text-[hsl(var(--slate-deep))]">
                      The Old Way
                    </h3>
                    <p className="text-sm text-[hsl(var(--slate-medium))]">
                      Qualitative assessments
                    </p>
                  </div>
                </div>

                {/* Example output */}
                <div className="mb-8 p-4 rounded-xl bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                  <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider mb-2">
                    Typical Output
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-600 text-sm font-medium">
                      HIGH
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-600 text-sm font-medium">
                      MEDIUM
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-600 text-sm font-medium">
                      LOW
                    </div>
                  </div>
                </div>

                {/* List items */}
                <ul className="space-y-4">
                  {oldWayItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-5 h-5 mt-0.5 rounded-full bg-[hsl(var(--destructive))]/10 flex items-center justify-center flex-shrink-0">
                        <X className="w-3 h-3 text-[hsl(var(--destructive))]" />
                      </div>
                      <div>
                        <div className="text-[hsl(var(--slate-dark))] font-medium">
                          {item.text}
                        </div>
                        <div className="text-sm text-[hsl(var(--slate-medium))]">
                          {item.detail}
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* New Way Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[hsl(var(--amber))]/20 to-[hsl(var(--teal))]/20 rounded-2xl blur-xl opacity-60" />

            <div className="relative bg-white rounded-2xl p-8 border-2 border-[hsl(var(--amber))]/30 shadow-xl overflow-hidden">
              {/* Accent corner */}
              <div className="absolute top-0 right-0 w-24 h-24">
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-[hsl(var(--amber))]/10 to-transparent" />
              </div>

              <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-medium text-[hsl(var(--slate-deep))]">
                      The ImpactOT Way
                    </h3>
                    <p className="text-sm text-[hsl(var(--amber))]">
                      Financial quantification
                    </p>
                  </div>
                </div>

                {/* Example output */}
                <div className="mb-8 p-4 rounded-xl bg-[hsl(var(--amber))]/5 border border-[hsl(var(--amber))]/20">
                  <div className="text-xs font-medium text-[hsl(var(--amber))] uppercase tracking-wider mb-2">
                    Real Output
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[hsl(var(--slate-dark))]">
                        24-hour exposure:
                      </span>
                      <span className="font-mono font-semibold text-[hsl(var(--slate-deep))]">
                        £2,847,500
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[hsl(var(--slate-dark))]">
                        Hourly impact:
                      </span>
                      <span className="font-mono font-semibold text-[hsl(var(--amber))]">
                        £118,645/hr
                      </span>
                    </div>
                  </div>
                </div>

                {/* List items */}
                <ul className="space-y-4">
                  {newWayItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: 10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-5 h-5 mt-0.5 rounded-full bg-[hsl(var(--teal))]/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[hsl(var(--teal))]" />
                      </div>
                      <div>
                        <div className="text-[hsl(var(--slate-dark))] font-medium">
                          {item.text}
                        </div>
                        <div className="text-sm text-[hsl(var(--slate-medium))]">
                          {item.detail}
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <blockquote className="text-xl sm:text-2xl font-serif italic text-[hsl(var(--slate-dark))] max-w-3xl mx-auto">
            "When the board asks what downtime costs, you shouldn't have to
            guess. You should know{' '}
            <span className="text-gradient-amber not-italic font-semibold">
              exactly
            </span>
            ."
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
}
